import { useMemo } from 'react';
import { hashKey, useInfiniteQuery, useQuery } from '@tanstack/react-query';

import type { CommunicationDetails, SharedApi, SharedApiCommonQueryParams } from '@izimi/api';
import { THIRTY_MINUTES_MS, TWO_MINUTES_MS } from '@izimi/utils';

import { mapCommunicationToEntity } from '../communications';
import { mapDocumentToEntity } from '../documents';
import { sharedQueryKeys } from './cache';
import { mapSharedItemsToEntities } from './helpers';

const EXPIRING_COMMUNICATIONS_WILL_EXPIRE_IN_MS = 31 * 24 * 60 * 60 * 1000;
const REFETCH_THRESHOLD_MS = 1000;

type UsePagedSharedItemsProps = {
  pageSize?: number;
  enabled?: boolean;
} & SharedApiCommonQueryParams;

const createSharedModule = ({ sharedApi }: { sharedApi: SharedApi }) => {
  const usePagedSharedItems = ({
    pageSize = 15,
    enabled,
    ...params
  }: UsePagedSharedItemsProps = {}) => {
    const { data, isLoading, error, refetch, hasNextPage, fetchNextPage, isFetchingNextPage } =
      useInfiniteQuery({
        queryKey: sharedQueryKeys.getPagedSharedItems(params),
        initialPageParam: 0,
        queryFn: async ({ pageParam, queryKey }) =>
          sharedApi.getSharedItems({
            params: { offset: Number(pageParam), limit: pageSize, ...params },
            queryKeyHash: hashKey(queryKey),
          }),
        getNextPageParam: (lastPage, allPages) => {
          const totalFetchedItems = allPages.flatMap((page) => page.items).length;

          return totalFetchedItems < lastPage.total ? allPages.length * pageSize : undefined;
        },
        refetchInterval: ({ state: { data } }) => {
          const items = data?.pages
            .flatMap((page) => page.items)
            .filter(
              (item): item is CommunicationDetails =>
                item._collection === 'NOT_EXPIRING_COMMUNICATION_DETAILS',
            );
          let refetchInterval = 0;

          for (const item of items ?? []) {
            const expiresInMs = new Date(item.expirationDate).getTime() - Date.now();
            const refetchDelayMs =
              expiresInMs - EXPIRING_COMMUNICATIONS_WILL_EXPIRE_IN_MS + REFETCH_THRESHOLD_MS;

            if (
              expiresInMs > 0 &&
              expiresInMs < THIRTY_MINUTES_MS + EXPIRING_COMMUNICATIONS_WILL_EXPIRE_IN_MS
            ) {
              refetchInterval =
                refetchInterval !== 0 && refetchInterval < refetchDelayMs
                  ? refetchInterval
                  : refetchDelayMs;
            }
          }

          return refetchInterval > 0 ? refetchInterval : false;
        },
        enabled,
        staleTime: TWO_MINUTES_MS,
      });

    const sharedItems = useMemo(() => data?.pages.flatMap(mapSharedItemsToEntities), [data]);
    const metadata = useMemo(() => data?.pages[0]?.metadata, [data]);

    return {
      metadata,
      sharedItems,
      isLoading,
      error,
      total: data?.pages[0]?.total ?? 0,
      refetch,
      hasNextPage,
      fetchNextPage,
      isFetchingNextPage,
    };
  };

  const useExpiringCommunications = ({ limit }: { limit: number } = { limit: 6 }) => {
    const { data, ...res } = useQuery({
      queryKey: sharedQueryKeys.getExpiringCommunications({ limit }),
      queryFn: async () => sharedApi.getExpiringCommunications({ limit, offset: 0 }),
    });

    const expiringCommunications = useMemo(
      () =>
        mapSharedItemsToEntities({
          items: data?.items.map((item) => ({ ...item, _collection: data.collection })) ?? [],
        }),
      [data],
    );

    return {
      expiringCommunications,
      ...res,
    };
  };

  const useLastSharedItems = ({ limit }: { limit: number } = { limit: 6 }) => {
    const { data, ...rest } = useQuery({
      queryKey: sharedQueryKeys.getLastSharedItems({ limit }),
      queryFn: async () => sharedApi.getLastSharedItems({ limit }),
    });

    const lastSharedItems = useMemo(
      () =>
        data?.map((item) => {
          if ('sender' in item) {
            return mapCommunicationToEntity(item);
          }
          return mapDocumentToEntity(item);
        }),
      [data],
    );

    return {
      lastSharedItems,
      ...rest,
    };
  };

  return {
    usePagedSharedItems,
    useLastSharedItems,
    useExpiringCommunications,
  };
};

export default createSharedModule;
